import { effect } from "@angular/core";
import { GridApi, GridOptions, GridReadyEvent } from "ag-grid-enterprise";
import { TableParent } from "@bitwarden/web-vault/app/models/types/table.types";

import type { GridSizeChangedEvent } from "ag-grid-community/dist/types/core/events";
import { EarnDraftTableSubscription, TableSource } from "./earn-draft.table.subscription";
import { ColDef as AgColDef } from "ag-grid-community";
import { EarnTableView } from "@bitwarden/web-vault/app/models/types/transaction.types";
import { RedactionDescriptionRenderer } from "@bitwarden/web-vault/app/components/tables/earn-tables/renderers/description/description.cell-renderer";
import { RedactionPublishDateRenderer } from "@bitwarden/web-vault/app/components/tables/earn-tables/renderers/publish-date/publish-date.cell-renderer";
import { EarnTableStateEnum } from "@bitwarden/web-vault/app/models/enum/earn.enum";
import { EarnTableHeaderComponentParams } from "@bitwarden/web-vault/app/models/types/earn.types";
import { CheckboxCellRenderer } from "@bitwarden/web-vault/app/components/tables/earn-tables/renderers/select-all/checkbox.cell-renderer";
import { SelectAllHeaderRenderer } from "@bitwarden/web-vault/app/components/tables/earn-tables/renderers/select-all/select-all-header.renderer";
import { CheckboxHeaderRenderer } from "@bitwarden/web-vault/app/components/tables/earn-tables/renderers/header/header.renderer";
import {
  getRowClass,
  getRowHeight,
} from "@bitwarden/web-vault/app/components/tables/tables.utils.js";

type TransactionRedactionTableSource = TableSource;

export interface ColDefExtended<T> extends AgColDef<T> {
  headerComponentFramework?: any;
}

export type TransactionId = string;

export class EarnDraftTable extends EarnDraftTableSubscription {
  gridApi: GridApi;
  options: GridOptions<TransactionRedactionTableSource>;
  private selectedRowIndex: number;
  columnDefinitions: ColDefExtended<TransactionRedactionTableSource>[];

  states: EarnTableStateEnum[] = [
    EarnTableStateEnum.DRAFT,
    EarnTableStateEnum.PREVIEW,
    EarnTableStateEnum.PUBLISH,
  ];

  stateLabels: Record<EarnTableStateEnum, string> = {
    [EarnTableStateEnum.DRAFT]: "Draft Editing",
    [EarnTableStateEnum.PREVIEW]: "Privacy Preview",
    [EarnTableStateEnum.PUBLISH]: "Published Data",
  };

  /*   refresh = effect(() => {
    const selectedMask = this.earnService.selectedMask();
    this.gridApi?.refreshCells({ force: true });
  }); */

  redraw = effect(() => {
    const lookupMask = this.earnLookup.lookupMask();
    this.gridApi?.redrawRows();
  });

  constructor(parent: TableParent) {
    super();
    this.columnDefinitions = [
      {
        headerName: "",
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        suppressHeaderMenuButton: true,
        sortable: false,
        filter: false,
        pinned: "left",
        lockPosition: true,
        resizable: false,
        headerComponent: SelectAllHeaderRenderer,
        headerComponentParams: {
          isToggleDisabled: () => this.earnService.breadCrumbState() !== EarnTableStateEnum.DRAFT,
        },
        cellRenderer: CheckboxCellRenderer,
        cellRendererParams: {
          isRowHidden: (rowId: string) => !!this.earnService.selectedMask()?.rows[rowId],
          isToggleDisabled: () => this.earnService.breadCrumbState() !== EarnTableStateEnum.DRAFT,
        },
      },

      {
        field: "publishedAt",
        headerName: "Publish Date",
        minWidth: this.calculateMinWidth("Publish Date", false),
        filter: "agTextColumnFilter",
        suppressHeaderMenuButton: true,
        cellRenderer: RedactionPublishDateRenderer,
      },

      {
        field: "description",
        headerName: "Description",
        minWidth: this.calculateMinWidth("Description", true),
        filter: "agTextColumnFilter",
        suppressHeaderMenuButton: true,
        toolPanelClass: "custom-filter-column",
        headerComponent: CheckboxHeaderRenderer,
        cellRenderer: RedactionDescriptionRenderer,
      },
      {
        field: "account",
        headerName: "Account",
        minWidth: this.calculateMinWidth("Account", true),
        filter: "agTextColumnFilter",
        suppressHeaderMenuButton: true,
        headerComponent: CheckboxHeaderRenderer,
        cellRenderer: RedactionDescriptionRenderer,
      },
      {
        field: "transactionDate",
        headerName: "Date",
        minWidth: this.calculateMinWidth("Date", true),
        filter: "agTextColumnFilter",
        suppressHeaderMenuButton: true,
        headerComponent: CheckboxHeaderRenderer,
        cellRenderer: RedactionDescriptionRenderer,
      },
      {
        field: "inOut",
        headerName: "In/Out",
        minWidth: this.calculateMinWidth("In/Out", true),
        filter: "agTextColumnFilter",
        suppressHeaderMenuButton: true,
        headerComponent: CheckboxHeaderRenderer,
        cellRenderer: RedactionDescriptionRenderer,
      },
      {
        field: "currency",
        headerName: "Currency",
        minWidth: this.calculateMinWidth("Currency", true),
        filter: "agTextColumnFilter",
        suppressHeaderMenuButton: true,
        headerComponent: CheckboxHeaderRenderer,
        cellRenderer: RedactionDescriptionRenderer,
      },
      {
        field: "amount",
        headerName: "Amount",
        minWidth: this.calculateMinWidth("Amount", true),
        filter: "agTextColumnFilter",
        suppressHeaderMenuButton: true,
        headerComponent: CheckboxHeaderRenderer,
        cellRenderer: RedactionDescriptionRenderer,
      },
      {
        field: "source",
        headerName: "Source",
        minWidth: this.calculateMinWidth("Source", true),
        filter: "agTextColumnFilter",
        suppressHeaderMenuButton: true,
        headerComponent: CheckboxHeaderRenderer,
        cellRenderer: RedactionDescriptionRenderer,
      },
    ];

    this.options = {
      context: { parent },
      onGridReady: async (params: GridReadyEvent) => {
        this.gridApi = params.api;
      },
      onGridSizeChanged: (params: GridSizeChangedEvent) => {
        params.api.sizeColumnsToFit();
      },
      onFilterChanged: () => {
        this.applyNewArrayToMask();
      },
      onPaginationChanged: (params) => {
        const newPageSize = params.api.paginationGetPageSize();
        this.earnService.defaultPageSize.set(newPageSize);
      },
      suppressCellFocus: true,
      suppressContextMenu: true,
      rowDragManaged: false,
      pagination: true,
      paginationPageSize: this.earnService.defaultPageSize(),
      paginationPageSizeSelector: [5, 10, 20, 50, 100],
      defaultColDef: {
        flex: 1,
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
      },
      columnDefs: this.columnDefinitions,
      getRowHeight: getRowHeight,

      getRowClass: (params) => getRowClass(params, this.selectedRowIndex),
      getRowId: (params) => params.data.id,
    };
  }

  applyNewArrayToMask() {
    const appliedToArray: string[] = [];
    this.gridApi.forEachNodeAfterFilterAndSort((n) => appliedToArray.push(n.data.id));
    this.earnService.onAppliedToArrayChange(appliedToArray);
  }

  private calculateMinWidth(headerText: string, hasCheckbox: boolean): number {
    // Approximate character width in pixels
    const charWidth = 8;
    // Checkbox width + padding
    const checkboxWidth = hasCheckbox ? 40 : 0;
    // Additional padding for cell content
    const padding = 4;

    const textWidth = headerText.length * charWidth;
    const totalWidth = textWidth + checkboxWidth + padding;

    // Set a minimum of 80px and maximum of 200px
    return Math.max(80, Math.min(totalWidth, 200));
  }
}
